import React from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom';

import Jumbotron from '../components/jumbotron/jumbotron'
import TableOfConents from '../components/table-of-contents/table-of-contents'
import ProductsList from '../components/products-list/products-list'
import ReviewContent from '../components/review-content/review-content'
import Layout from '../components/layout'
import Table from "../components/table/table";
import SeoTags from "../components/seo-tags";
import StructuredData from "../components/structured-data";
import Breadcrumbs from "../components/breadcrumbs/breadcrumbs";
import SocialShare from "../components/social-share/social-share";
import HowDecided from "../components/how-decided/how-decided";
import WhyTrust from "../components/why-trust/why-trust";
import InlineRelated from "../components/inline-related/inline-related";
import WeRecommended from "../components/we-recommended/we-recommended";
import Author from "../components/author/author";

import * as utils from '../utils'

const CONCLUSION = 'Conclusion'
const BUYERS_GUIDE = 'Buyers guide'
const COMPARISON_TABLE = 'Comparison Table'

export const ReviewPage = (props) => {

  const {
    title,
    short_title,
    introduction,
    introduction_2,
    image,
    conclusion,
    buyers_guide_1,
    buyers_guide_2,
    faq,
    products,
    extra,
    created,
    updated,
    seo,
    related_reviews
  } = props.data.markdownRemark.frontmatter;

  const smallImages = props.data.allImageSharp.edges

  const currentPath = props.location.pathname;
  const slug = props.data.markdownRemark.fields.slug;

  const updatedTitle = utils.updateYearToCurrent(title)

  let buyersGuideTitle = null;
  let faqTitle = null;

  //  === TOC ===
  const tableOfContent = [
    { title: COMPARISON_TABLE, link: extra.tableTitle },
    {
      title: 'Products',
      link: [...products.map((p, i) => ({
        title: p.featured ? `${p.featured.toUpperCase()}: ${p.product_name}` : `${p.product_name}`,
        link: p.title
      }))],
    }
  ];

  if (buyers_guide_2 && buyers_guide_2.content) {
    buyersGuideTitle = short_title + ' Buying Guide';
    if (faq) buyersGuideTitle = buyersGuideTitle + ' & FAQ'
    tableOfContent.push({ title: BUYERS_GUIDE, link: buyersGuideTitle })
  }
  if (faq) {
    faqTitle = short_title + " FAQ";
    tableOfContent.push({ title: "FAQ", link: faqTitle })
  }
  tableOfContent.push({ title: CONCLUSION, link: CONCLUSION })
  //  === TOC END ===

  //  === RELATED ===
  let relatedArticles = [];
  if (related_reviews && related_reviews.length) {
    relatedArticles = related_reviews
      .filter((item) => item)
      .map((r) => {
        return Object.assign(r.frontmatter, {
          slug: r.fields.slug,
          image: utils.findResizedImage(r.frontmatter.image, smallImages)
        })
      })
  }
  //  === RELATED END ===

  const recommended = products.filter((prod) => prod.featured);

  return (
    <Layout>
      <article>
        <SeoTags
          title={`${utils.updateYearToCurrent(seo.title)}`}
          description={`${utils.updateYearToCurrent(seo.description)}`}
          image={image}
          image_alt={updatedTitle}
          link={[
            { rel: 'amphtml', href: `/amp${slug}` }
          ]}
          created={created}
          updated={updated}
          isReview={true}
          pagePath={currentPath}
          author={seo.author}
        />
        <StructuredData
          title={updatedTitle}
          image={image}
          created={created}
          updated={updated}
          isReview={true}
          pagePath={currentPath}
          isBreadcrumbs={true}
          products={products}
          introduction={introduction}
        />
        <Jumbotron
          title={updatedTitle}
          image={image}
          alt={updatedTitle}
          isForReview={true}
        />

        <Breadcrumbs currentPageTitle={short_title} />

        <Author name={seo.author} created={created} updated={updated} />

        <ReviewContent content={introduction} isWide />

        <WhyTrust />

        {extra.isWithRecommended ? <WeRecommended recommended={recommended} /> : null}

        {introduction_2 && <ReviewContent content={introduction_2} />}

        <TableOfConents links={tableOfContent} />

        {relatedArticles.length !== 0 ? <InlineRelated articles={relatedArticles} /> : null}

        {buyers_guide_1 && <ReviewContent content={buyers_guide_1} />}

        {extra.isWithTable ? <Table tableTitle={extra.tableTitle} products={products} /> : null}

        {extra.how_decided ? <HowDecided data={extra.how_decided} /> : null}


        <ProductsList title={short_title} products={products} />

        {buyers_guide_2 && <ReviewContent content={buyers_guide_2.intro} title={buyersGuideTitle} />}
        {buyers_guide_2 && buyers_guide_2.content && (
          buyers_guide_2.content.map(({ body, title }, i) => (
            <ReviewContent key={i} content={body} title={title} />
          ))
        )}

        {faq && <ReviewContent title={faqTitle} />}
        {faq && faq.map(({ body, title }, i) => (
          <ReviewContent key={i} content={body} title={"Q: " + title} headingLevel="h3" />
        ))}

        <ReviewContent content={conclusion} title={CONCLUSION} />

        <SocialShare image={image} title={updatedTitle} pagePath={currentPath} />

      </article>
    </Layout>
  )
};

ReviewPage.propTypes = {
  data: PropTypes.object,
  location: PropTypes.object,
};

export default withRouter(ReviewPage);

export const pageQuery = graphql`
  query ReviewPageById($id: String!) {
    markdownRemark(id: { eq: $id }) {
      id
      fields {
        slug
      }
      frontmatter {
        templateKey
        slug
        created
        updated
        seo {
          title
          description
          author
        }
        extra {
          isWithTable
          isWithRecommended
          tableTitle
          tags_list
          how_decided
        }
        title
        subheading
        short_title
        image
        introduction
        introduction_2
        buyers_guide_1
        products {
          title
          subtitle
          image
          image_gallery {
            image_url
            image_alt
          }
          youtube
          body
          prons
          cons
          bottom_line
          amazon_link
          product_name
          price
          featured
          rating
          table_features {
            feature_name
            feature_key
            feature_value
          }
        }
        buyers_guide_2 {
          intro
          content {
            title
            body
          }
        }
        faq {
          title
          body
        }
        conclusion
        related_reviews {
          id
          fields {
            slug
          }
          frontmatter {
            title
            introduction
            image
          }
        }
      }
    }
    allImageSharp(filter: {id: {regex: "/jumbotron/"}}) {
      edges{
        node {
        ... on ImageSharp {
          resize(width: 200) {
            originalName
            src
            }
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/templates/review-page.js